import { useBlocker } from '@tanstack/react-router';
import { Plus, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { isNameTitle, nameEquivalencesSchema, normaliseName, type NameEquivalencesView } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Field } from '../../../components/Field';
import { Input } from '../../../components/Input';
import { Modal } from '../../../components/Modal';
import { SaveBar } from '../../../components/SaveBar';
import { SkeletonLines } from '../../../components/Skeleton';
import { ForbiddenState, ServerErrorState } from '../../../components/system-states/SystemState';
import { errorMessage } from '../../../lib/api-client';
import { formatNumber } from '../../../lib/format';
import { useCanEdit, useCanView } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';
import { useNameTable, useSaveNameTable, useTryName } from '../api/name-matching-api';
import { ListTable, type ListColumn } from '../components/simple-list/ListTable';
import { ListNoMatch, ListSearch, ListToolbar, PanelSection, SidePanel, SimpleListPage, usePanelMode } from '../components/simple-list/SimpleList';
import { useListAddress } from '../components/simple-list/use-list-address';
import styles from './NameMatchingPage.module.css';

const PATH = '/admin/name-matching';
const TRY_DEBOUNCE_MS = 250;

/** One name as the page edits it: the main spelling, and the others typed comma-separated (as before). */
export interface NameRow {
  readonly key: string;
  readonly main: string;
  readonly others: string;
}

let nextRow = 0;
function rowKey(): string {
  nextRow += 1;
  return `name-${nextRow}`;
}

/**
 * §L2's transliteration table on its own admin screen (S31 Q23), in the simple-list pattern (Admin redesign batch 2,
 * option A): one row per name, its other spellings typed in place, "Names" (how many stored names use it), and beside
 * the table "Try a name" and "Add a name". Saving matches every stored name again, so the next duplicate check already
 * uses the new spellings. It is saved with the version it was edited from: someone else's save in between is never
 * overwritten. Leaving with unsaved changes asks first, inside the app as well as when the tab closes. Admin only.
 */
export function NameMatchingPage(): ReactNode {
  const canView = useCanView('admin_config');
  const table = useNameTable(canView);
  if (!canView) return <ForbiddenState resource="name matching" contact="Your admin" />;
  if (table.isPending) return <SkeletonLines count={8} />;
  if (table.error && !table.data) return <ServerErrorState onRetry={() => void table.refetch()} />;
  return <NameTableEditor saved={table.data} />;
}

function NameTableEditor({ saved }: { readonly saved: NameEquivalencesView }): ReactNode {
  const canEdit = useCanEdit('admin_config');
  const save = useSaveNameTable();
  const show = useToasts((state) => state.show);
  const [address, setAddress] = useListAddress(PATH);
  const isPhone = usePanelMode() === 'sheet';
  const query = address.q ?? '';
  // The table being edited, and the saved version it started from. A newer saved table is taken while nothing is
  // changed; with changes, the page says someone else saved and offers theirs.
  const [base, setBase] = useState(saved);
  const [rows, setRows] = useState<readonly NameRow[]>(() => rowsOf(saved.groups));
  const draft = groupsOfRows(rows);
  const isDirty = canonical(draft.groups) !== canonical(base.groups);
  const isStale = saved.version !== base.version;

  // Adjusted while rendering, not in an effect: the newer table is on screen in the same paint.
  if (isStale && !isDirty) {
    setBase(saved);
    setRows(rowsOf(saved.groups));
  }

  const check = nameEquivalencesSchema.safeParse({ groups: draft.groups });
  const problems = problemsByRow(check, draft.rowOf);
  const problem = check.success ? null : (check.error.issues[0]?.message ?? 'Check the spellings.');

  const discard = (): void => setRows(rowsOf(base.groups));
  const takeTheirs = (): void => {
    setBase(saved);
    setRows(rowsOf(saved.groups));
  };

  const submit = async (): Promise<void> => {
    if (!isDirty || problem !== null || isStale) return;
    try {
      const view = await save.mutateAsync({ groups: draft.groups, basedOn: base.version });
      setBase(view);
      setRows(rowsOf(view.groups));
      show({ tone: 'success', message: 'Name matching saved', detail: refreshedDetail(view.namesRefreshed ?? 0) });
    } catch (cause) {
      show({ tone: 'danger', message: "Name matching wasn't saved", detail: errorMessage(cause) });
    }
  };

  const shownRows = rows.filter((row) => matches(row, query));
  const othersCell = (row: NameRow): ReactNode => (
    <span className={styles.cell}>
      <Input
        aria-label={`Other spellings of ${row.main || 'this name'}, separated by commas`}
        value={row.others}
        disabled={!canEdit}
        isInvalid={problems.has(row.key)}
        onChange={(event) => setRows(update(rows, row.key, { others: event.target.value }))}
      />
      <RowNote row={row} problem={problems.get(row.key)} />
    </span>
  );
  // On a phone the two boxes stack in one cell, so a row never runs off the screen (review W8).
  const columns: ListColumn<NameRow>[] = isPhone
    ? []
    : [
        { key: 'others', header: 'Also written as', render: othersCell },
        { key: 'names', header: 'Names', isNumeric: true, render: (row) => namesOf(saved, row) },
      ];

  return (
    <>
      <SimpleListPage
        crumb="Admin · System"
        title="Name matching"
        total={rows.length}
        lede="Spellings of one name, so “Mohammad Rahaman” and “Muhammad Rahman” are recognised as the same person when checking for duplicates. Saving reads every stored name again."
        toolbar={<ListToolbar search={<ListSearch label="Find a spelling" value={query} onChange={(next) => setAddress({ q: next })} />} />}
        notice={
          isStale && (
            <p className={styles.stale} role="alert">
              Someone else saved name matching while you were editing, so yours can’t be saved over it.{' '}
              <Button variant="ghost" size="sm" onClick={takeTheirs}>
                Load theirs — your changes here are dropped
              </Button>
            </p>
          )
        }
        panel={
          <SidePanel mode="docked" label="Try a name and add one" title="Try a name">
            <TryName rows={rows} isDirty={isDirty} />
            {canEdit && <AddName onAdd={(row) => setRows([...rows, row])} />}
          </SidePanel>
        }
      >
        <ListTable
          label="Names and their spellings"
          name={{
            header: 'Main spelling',
            label: (row) => row.main,
            render: (row) => (
              <span className={styles.main}>
                <Input aria-label={`Main spelling of name ${rows.indexOf(row) + 1}`} value={row.main} disabled={!canEdit} isInvalid={problems.has(row.key)} onChange={(event) => setRows(update(rows, row.key, { main: event.target.value }))} />
              </span>
            ),
          }}
          columns={columns}
          groups={[{ key: 'all', rows: shownRows }]}
          idOf={(row) => row.key}
          selectedId={null}
          phoneLine={
            isPhone
              ? (row) => (
                  <>
                    {othersCell(row)}
                    {namesOf(saved, row)}
                  </>
                )
              : undefined
          }
          trailing={(row) => canEdit && <Button variant="ghost" size="sm" icon={Trash2} aria-label={`Remove ${row.main || 'this name'}`} onClick={() => setRows(rows.filter((other) => other.key !== row.key))} />}
          empty={rows.length === 0 ? <span>No names yet. Add one beside the table.</span> : <ListNoMatch what="spelling" query={query} onClear={() => setAddress({ q: undefined })} />}
        />
        {canEdit && (
          <SaveBar status={<SaveStatus isDirty={isDirty} isStale={isStale} problem={isDirty ? problem : null} changed={changedCount(base.groups, draft.groups)} />}>
            <Button variant="ghost" disabled={!isDirty || save.isPending} onClick={discard}>
              Discard
            </Button>
            <Button variant="solid" isPending={save.isPending} disabled={!isDirty || problem !== null || isStale} onClick={() => void submit()}>
              Save
            </Button>
          </SaveBar>
        )}
      </SimpleListPage>
      <LeaveGuard isDirty={isDirty && !save.isPending} />
    </>
  );
}

/** Under a row: what is wrong with it, or that its main spelling is dropped as a title before matching. */
function RowNote({ row, problem }: { readonly row: NameRow; readonly problem: string | undefined }): ReactNode {
  if (problem !== undefined) return <span className={styles.problem}>{problem}</span>;
  if (isNameTitle(row.main)) return <span className={styles.note}>“{row.main.trim()}” is read as a title and left out of every name, so this row never matches anything.</span>;
  return null;
}

function SaveStatus({ isDirty, isStale, problem, changed }: { readonly isDirty: boolean; readonly isStale: boolean; readonly problem: string | null; readonly changed: number }): ReactNode {
  if (isStale) return <span className={styles.problem}>Someone else saved meanwhile.</span>;
  if (problem !== null) return <span className={styles.problem}>{problem}</span>;
  if (!isDirty) return <span>No unsaved changes</span>;
  return (
    <span>
      <strong>{changed === 1 ? '1 name changed' : `${formatNumber(changed)} names changed`}</strong> · not saved yet
    </span>
  );
}

/**
 * "Try a name": how the table as it stands on screen reads the name (`normaliseName`, live), and — only while nothing
 * is unsaved, because stored names were read with the saved table — how many stored names read the same.
 */
function TryName({ rows, isDirty }: { readonly rows: readonly NameRow[]; readonly isDirty: boolean }): ReactNode {
  const [name, setName] = useState('');
  const [asked, setAsked] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setAsked(name), TRY_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [name]);
  const matchesNow = useTryName(asked, !isDirty);
  const readAs = name.trim() === '' ? '' : normaliseName(name, groupsOfRows(rows).groups);
  return (
    <PanelSection>
      <Field label="A name" hint="As someone might type it on an application.">
        {({ inputId, describedBy }) => <Input id={inputId} aria-describedby={describedBy} value={name} maxLength={200} placeholder="e.g. Md. Rahaman Hossain" onChange={(event) => setName(event.target.value)} />}
      </Field>
      <dl className={styles.tried}>
        <dt>Read as</dt>
        <dd>{readAs === '' ? '—' : readAs}</dd>
        <dt>Matches</dt>
        <dd aria-live="polite">{matchText(name, readAs, isDirty, matchesNow.data, matchesNow.error ? errorMessage(matchesNow.error) : null)}</dd>
      </dl>
    </PanelSection>
  );
}

function matchText(name: string, readAs: string, isDirty: boolean, result: { readonly readAs: string; readonly candidates: number } | undefined, error: string | null): string {
  if (name.trim().length < 2) return '—';
  if (isDirty) return 'Save first: stored names were read with the saved spellings.';
  if (error !== null) return error;
  if (!result || result.readAs !== readAs) return 'Counting…';
  if (readAs === '') return 'Nothing is left once titles are taken out.';
  return result.candidates === 1 ? '1 stored candidate reads the same.' : `${formatNumber(result.candidates)} stored candidates read the same.`;
}

function AddName({ onAdd }: { readonly onAdd: (row: NameRow) => void }): ReactNode {
  const [typed, setTyped] = useState('');
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const add = (): void => {
    const spellings = spellingsOf(typed);
    const [main, ...others] = spellings;
    if (main === undefined || others.length === 0) {
      setError('Give at least two spellings, separated by commas.');
      return;
    }
    onAdd({ key: rowKey(), main, others: others.join(', ') });
    setTyped('');
    setError(null);
    inputRef.current?.focus();
  };
  return (
    <PanelSection title="Add a name">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          add();
        }}
      >
        <Field label="Spellings" hint="Comma-separated; the first is the main spelling. Saved with the rest." error={error ?? undefined}>
          {({ inputId, describedBy, isInvalid }) => <Input ref={inputRef} id={inputId} aria-describedby={describedBy} isInvalid={isInvalid} value={typed} placeholder="e.g. begum, begam" onChange={(event) => setTyped(event.target.value)} />}
        </Field>
      </form>
      <div className={styles.addRow}>
        <Button variant="outline" size="sm" icon={Plus} disabled={typed.trim() === ''} onClick={add}>
          Add
        </Button>
      </div>
    </PanelSection>
  );
}

/**
 * Unsaved changes are never dropped silently (06-qol §4; review R17): moving to another page inside the app asks here,
 * and closing or reloading the tab asks through the browser. Changing the search on this page is not leaving it.
 */
function LeaveGuard({ isDirty }: { readonly isDirty: boolean }): ReactNode {
  const blocker = useBlocker({
    shouldBlockFn: ({ current, next }) => isDirty && current.pathname !== next.pathname,
    enableBeforeUnload: () => isDirty,
    withResolver: true,
  });
  if (blocker.status !== 'blocked') return null;
  return (
    <Modal
      isOpen
      onClose={blocker.reset}
      title="Leave without saving?"
      description="Your changes to name matching haven’t been saved. Leaving drops them."
      footer={
        <>
          <Button variant="ghost" onClick={blocker.reset}>
            Stay
          </Button>
          <Button variant="dangerSolid" onClick={blocker.proceed}>
            Leave without saving
          </Button>
        </>
      }
    >
      <span />
    </Modal>
  );
}

// --- The draft ------------------------------------------------------------------------------------------------

export function rowsOf(groups: readonly (readonly string[])[]): readonly NameRow[] {
  return groups.map((group) => ({ key: rowKey(), main: group[0] ?? '', others: group.slice(1).join(', ') }));
}

export function spellingsOf(text: string): readonly string[] {
  return text
    .split(',')
    .map((word) => word.trim().toLowerCase())
    .filter((word) => word !== '');
}

/** The rows as the table they make, blank rows left out, with which row each group came from. */
export function groupsOfRows(rows: readonly NameRow[]): { readonly groups: readonly (readonly string[])[]; readonly rowOf: readonly string[] } {
  const groups: (readonly string[])[] = [];
  const rowOf: string[] = [];
  for (const row of rows) {
    const spellings = [...spellingsOf(row.main), ...spellingsOf(row.others)];
    if (spellings.length === 0) continue;
    groups.push(spellings);
    rowOf.push(row.key);
  }
  return { groups, rowOf };
}

/** The table in the server's stable order (by main spelling, the rest A to Z), to tell whether anything changed. */
export function canonical(groups: readonly (readonly string[])[]): string {
  const sorted = groups.map(([main = '', ...others]) => [main, ...[...others].sort()]).sort((a, b) => (a[0] ?? '').localeCompare(b[0] ?? ''));
  return JSON.stringify(sorted);
}

/** How many names differ between two tables: added, removed or changed. */
export function changedCount(before: readonly (readonly string[])[], after: readonly (readonly string[])[]): number {
  const key = (group: readonly string[]): string => canonical([group]);
  const was = new Set(before.map(key));
  const now = new Set(after.map(key));
  const added = [...now].filter((group) => !was.has(group)).length;
  const removed = [...was].filter((group) => !now.has(group)).length;
  return Math.max(added, removed);
}

function problemsByRow(check: ReturnType<typeof nameEquivalencesSchema.safeParse>, rowOf: readonly string[]): ReadonlyMap<string, string> {
  const out = new Map<string, string>();
  if (check.success) return out;
  for (const issue of check.error.issues) {
    const index = issue.path[0] === 'groups' && typeof issue.path[1] === 'number' ? issue.path[1] : -1;
    const key = rowOf[index];
    if (key !== undefined && !out.has(key)) out.set(key, issue.message);
  }
  return out;
}

function update(rows: readonly NameRow[], key: string, patch: Partial<NameRow>): readonly NameRow[] {
  return rows.map((row) => (row.key === key ? { ...row, ...patch } : row));
}

function matches(row: NameRow, query: string): boolean {
  const needle = query.trim().toLowerCase();
  return needle === '' || row.main.toLowerCase().includes(needle) || row.others.toLowerCase().includes(needle);
}

/** "1,840 names", counted under the saved table; `—` for a main spelling not saved yet. */
function namesOf(saved: NameEquivalencesView, row: NameRow): string {
  const count = saved.namesByMain?.[row.main.trim().toLowerCase()];
  if (count === undefined) return '—';
  return count === 1 ? '1 name' : `${formatNumber(count)} names`;
}

/** S31 (Q23): stored names are matched again under the new table straight away. */
function refreshedDetail(count: number): string {
  if (count === 0) return 'Every stored name already matched these spellings.';
  return `${count} stored ${count === 1 ? 'name was' : 'names were'} updated, so duplicates are found with the new spellings.`;
}
